import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
    Typography,
    Box,
    Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateField, useGetOne, useNotify, useRefresh } from 'react-admin';
import { deleteResource, type ResourceId } from '@wwf/sdk';
import { useResource } from '@app/misc/useResource';
import { AntennaLightCard } from '@app/antenna/show/AntennaLightCard';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import { MissionLightCard } from '@app/mission/show/MissionLightCard';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { SectionDivider } from '@app/misc/SectionDivider';
import { PdfPreview } from './PdfPreview';
import {
    MediaResource,
    resourceDisplayName,
    resourceMediaKind,
} from './resourceMediaKind';

interface MediaDialogProps {
    selectedMedia: MediaResource | null | undefined;
    onClose: () => void;
}

export const MediaDialog = ({ selectedMedia, onClose }: MediaDialogProps) => {
    return (
        <Dialog
            open={!!selectedMedia}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    color: 'white',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            {selectedMedia && (
                <>
                    <DialogTitle>
                        {resourceDisplayName(selectedMedia) ||
                            `${selectedMedia.type} file`}
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'stretch',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                textAlign: 'center',
                            }}
                        >
                            <Box
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <RenderModalContent media={selectedMedia} />
                            </Box>
                            {selectedMedia.description && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 2,
                                        color: 'white',
                                        flexShrink: 0,
                                    }}
                                >
                                    {selectedMedia.description}
                                </Typography>
                            )}
                        </Box>
                        <MediaDialogSidebar media={selectedMedia} />
                    </DialogContent>
                    <MediaDialogActions
                        media={selectedMedia}
                        onClose={onClose}
                    />
                </>
            )}
        </Dialog>
    );
};

const downloadMediaFile = async (url: string, filename: string) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Failed to download file');
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } finally {
        URL.revokeObjectURL(blobUrl);
    }
};

const MediaDialogActions = ({
    media,
    onClose,
}: {
    media: MediaResource;
    onClose: () => void;
}) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const { data: src, isLoading: isLoadingSrc } = useResource(media.id);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleDownload = async () => {
        if (!src) {
            notify('Download URL is not available yet', { type: 'warning' });
            return;
        }
        setDownloading(true);
        try {
            await downloadMediaFile(src, media.name ?? 'media');
        } catch (error) {
            notify(
                error instanceof Error
                    ? error.message
                    : 'Failed to download media',
                { type: 'error' }
            );
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await deleteResource(media.id as ResourceId);
            notify('Media deleted', { type: 'success' });
            setConfirmDeleteOpen(false);
            refresh();
            onClose();
        } catch (error) {
            notify(
                error instanceof Error
                    ? error.message
                    : 'Failed to delete media',
                { type: 'error' }
            );
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <DialogActions>
                <Button
                    onClick={handleDownload}
                    color="inherit"
                    startIcon={<DownloadIcon />}
                    disabled={downloading || isLoadingSrc || !src}
                >
                    {downloading ? 'Downloading…' : 'Download'}
                </Button>
                <Button
                    onClick={() => setConfirmDeleteOpen(true)}
                    color="error"
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    disabled={deleting}
                >
                    Delete
                </Button>
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
            </DialogActions>
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => !deleting && setConfirmDeleteOpen(false)}
            >
                <DialogTitle>Delete media permanently?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This media will be definitively deleted from the
                        servers. It cannot be recovered.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDeleteOpen(false)}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        startIcon={<DeleteIcon />}
                    >
                        {deleting ? 'Deleting…' : 'Delete permanently'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const mediaLinkageIds = (media: MediaResource) => ({
    authorId: 'authorId' in media ? media.authorId : null,
    antennaId: 'antennaId' in media ? media.antennaId : null,
    missionId: 'missionId' in media ? media.missionId : null,
});

const dateFieldOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
} as const;

const MediaDialogSidebar = ({ media }: { media: MediaResource }) => {
    const { authorId, antennaId, missionId } = mediaLinkageIds(media);

    const { data: author } = useGetOne(
        'member',
        { id: authorId! },
        { enabled: !!authorId }
    );
    const { data: antenna } = useGetOne(
        'antenna',
        { id: antennaId! },
        { enabled: !!antennaId }
    );
    const { data: mission } = useGetOne(
        'mission',
        { id: missionId! },
        { enabled: !!missionId }
    );

    return (
        <Box
            sx={{
                width: 280,
                flexShrink: 0,
                bgcolor: 'background.paper',
                color: 'text.primary',
                borderRadius: 1,
                p: 2,
                alignSelf: 'stretch',
            }}
        >
            <Stack gap={1}>
                <SectionDivider title="Details" dividerMarginBottom={0.5} />
                <LabelValueRow
                    label="Uploaded on"
                    value={
                        <DateField
                            source="createdAt"
                            record={media}
                            showTime
                            options={dateFieldOptions}
                        />
                    }
                />
                {author ? <MemberLightCard record={author} /> : null}
                {missionId && (
                    <>
                        <SectionDivider
                            title="Mission"
                            dividerMarginBottom={0.5}
                        />
                        {mission ? (
                            <MissionLightCard record={mission} twoLines />
                        ) : null}
                        {antenna ? <AntennaLightCard record={antenna} /> : null}
                    </>
                )}
            </Stack>
        </Box>
    );
};

const RenderModalContent = ({ media }: { media: MediaResource }) => {
    const { data: src } = useResource(media?.id);

    const kind = resourceMediaKind(media.type);
    if (kind === 'image') {
        return (
            <Box
                component="img"
                src={src ?? undefined}
                alt={media.alt || resourceDisplayName(media) || 'Media'}
                sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                }}
            />
        );
    }

    if (kind === 'video') {
        return (
            <Box
                component="video"
                controls
                sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                }}
            >
                <source src={src ?? undefined} type="video/mp4" />
                Your browser does not support the video tag.
            </Box>
        );
    }

    if (kind === 'audio') {
        return (
            <Box sx={{ width: '100%', maxWidth: 400 }}>
                <audio controls style={{ width: '100%' }}>
                    <source src={src ?? undefined} type="audio/mpeg" />
                    Your browser does not support the audio tag.
                </audio>
            </Box>
        );
    }

    // Check if document is a PDF
    if (kind === 'document' && media.mimeType === 'application/pdf') {
        return (
            <PdfPreview
                src={src ?? undefined}
                name={resourceDisplayName(media) || 'PDF Document'}
            />
        );
    }

    // For other document types, show a link to download/view
    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
                {resourceDisplayName(media) || 'Document'}
            </Typography>
            <Button
                variant="contained"
                href={src ?? ''}
                target="_blank"
                rel="noopener noreferrer"
            >
                Open Document
            </Button>
        </Box>
    );
};
